import React from 'react';
import clsx from 'clsx';
import { Container, createStyles, makeStyles, type Theme } from '@material-ui/core';
import {
  type AfterPageProps,
  createApiClient,
  createInitialPropsRedirectToLogin,
  makeAfterComponent,
  wrapLayout,
} from '../../@sprinx/react-after-razzle';
import commonThingsToLoad from '../../api/commonThingsToLoad';
import useTranslateWithFallback from '../../hooks/useTranslateWithFallback';
import { projectName } from '../../config';
import InfoLayout from '../../layouts/InfoLayout/InfoLayout';
import { FTP_URL, useAdminConfig } from '../../adminConfig';
import Carousel from './components/Carousel';
import SimpleImage from './components/SimpleImage';

export type HomePageProps = AfterPageProps;

export type HomePageClassNameKey =
  | 'root'
  | 'welcomeText'
  | 'mainBanner'
  | 'recommendedProducts'
  | 'brands'
  | 'brand'
  | 'interspaces'
  | 'srOnly';

const useStyles = makeStyles((theme: Theme) =>
  createStyles<HomePageClassNameKey, {}>({
    root: {
      textAlign: 'center',
      fontSize: theme.typography.h5.fontSize,
      fontWeight: theme.typography.fontWeightBold,
      color: theme.palette.primary.main,
    },
    welcomeText: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: theme.spacing(2, 0),
    },
    mainBanner: {
      width: 'inherit',
    },
    recommendedProducts: {
      display: 'flex',
      justifyContent: 'center',
      padding: theme.spacing(5, 0),
      border: `3px solid ${theme.palette.primary.main}`,
    },
    brands: {
      display: 'flex',
      flexWrap: 'wrap',
      columnGap: theme.spacing(2),
      rowGap: theme.spacing(4),
      justifyContent: 'center',
      maxWidth: theme.spacing(96),
      margin: theme.spacing(4, 'auto', 4, 'auto'),
    },
    brand: {
      width: theme.spacing(12),
      height: 'auto',
      margin: theme.spacing(1),
      objectFit: 'contain',
      [theme.breakpoints.up('sm')]: {
        width: theme.spacing(16),
      },
      [theme.breakpoints.up('md')]: {
        width: theme.spacing(20),
      },
    },
    interspaces: {
      margin: theme.spacing(4, 0, 2, 0),
    },
    srOnly: {
      position: 'absolute',
      left: '-9999px',
    },
  }),
);

const mainBrandsImages: { alt: string; src: string }[] = [
  {
    alt: 'SCHWARZKOPF PROFESSIONAL',
    src: `/${projectName}/homepage/mainBrands/SKP_Logo_SVG_black.svg`,
  },
  {
    alt: 'AUTHENTIC BEAUTY CONCEPT',
    src: `/${projectName}/homepage/mainBrands/ABC_Logo_SVG_black.svg`,
  },
  {
    alt: 'STMNT',
    src: `/${projectName}/homepage/mainBrands/STMNT_Logo_SVG_black.svg`,
  },
  {
    alt: 'OLYMP',
    src: `/${projectName}/homepage/mainBrands/OLYMP_Logo_SVG_black.svg`,
  },
  {
    alt: 'INDOLA',
    src: `/${projectName}/homepage/mainBrands/Indola_Logo_SVG_black.svg`,
  },
  {
    alt: 'ACCA KAPPA',
    src: `/${projectName}/homepage/mainBrands/AccaKappa_Logo_SVG_black.svg`,
  },
  {
    alt: 'FRAMAR',
    src: `/${projectName}/homepage/mainBrands/Framar_Logo_WEBP_black.webp`,
  },
];

const HomePage: React.FC<HomePageProps> = () => {
  const classes = useStyles();
  const t = useTranslateWithFallback();
  const { carousel } = useAdminConfig();

  const remapedCarousel =
    carousel
      ?.map((item) => ({
        alt: item.title ?? '',
        href: item.url,
        src: item.path ? `${FTP_URL}${item.path.startsWith('/') ? item.path.substring(1) : item.path}` : '',
        title: item.title,
      }))
      .reverse() || [];

  return (
    <Container className={classes.root}>
      <h1 className={classes.srOnly}>{t('homePage/title')}</h1>
      <div className={classes.welcomeText}>
        <span>{t('homePage/welcomeText/part1')}</span>
        <span>{t('homePage/welcomeText/part2')}</span>
      </div>
      {mainBrandsImages && mainBrandsImages.length > 0 && (
        <div className={classes.brands}>
          {mainBrandsImages.map((image, idx) => (
            <img
              key={`brand-${idx}-${image.alt}`}
              src={image.src}
              alt={image.alt}
              title={image.alt}
              className={classes.brand}
            />
          ))}
        </div>
      )}
      {remapedCarousel.length > 0 ? (
        <Carousel items={remapedCarousel} className={classes.interspaces} />
      ) : (
        <SimpleImage
          className={clsx(classes.mainBanner, classes.interspaces)}
          alt='Main Banner'
          src={`/${projectName}/homepage/homepage.png`}
        />
      )}
      {/* <div className={clsx(classes.recommendedProducts, classes.interspaces)}>
        TOP produkty, bestsellers, sezona ...
      </div> */}
    </Container>
  );
};

HomePage.displayName = 'HomePage';

export default makeAfterComponent(
  wrapLayout<HomePageProps>(HomePage, InfoLayout),
  async ({ match, initialAppProps }) => {
    try {
      if (initialAppProps) {
        const appProps = initialAppProps;
        const apiClient = createApiClient({ accessToken: appProps.auth?.accessToken });
        const commonThings = await Promise.all(commonThingsToLoad.promises({ apiClient }));
        appProps.propagateToState({
          ...commonThingsToLoad.toRegister(commonThings),
        });
      }
      return {};
    } catch (error) {
      if (error.response?.status === 401) {
        return createInitialPropsRedirectToLogin({ match });
      }
      return { error };
    }
  },
);
